import { c as _c } from "react/compiler-runtime";
import { UseQueryResult, useQueries } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { publicApiUrlAtom } from '@/atoms/processEnvAtom';
import { CategoryIdsByProductType, getCategoryIdsByProductType } from '@/utility/couponValidation';
import { AppError } from '@/utility/errorClasses';
const SIX_HOURS = 6 * 60 * 60 * 1000;
export const productTypeDepartmentsQueryOptions = (publicApiUrl: string, shopId: string) => ({
  queryKey: ['productTypeDepartments', shopId],
  queryFn: async () => {
    const url = `${publicApiUrl}/shops/${shopId}/productTypeDepartments?fullData=true&apiKey=mp-next`;
    // Plain fetch instead of the client `get` helper: its `x-pathname`/`x-search` headers are not
    // allowed by the public API's CORS policy, so the preflight would fail.
    const response = await fetch(url);
    if (!response.ok) {
      throw new AppError(`GET ${url} failed with status ${response.status}`, response.status);
    }
    return (await response.json()) as ProductTypeDepartmentResponse;
  },
  staleTime: SIX_HOURS
});
const selectCategoryIds = (response: ProductTypeDepartmentResponse) => getCategoryIdsByProductType(response.productTypeDepartments);
const combine = (results: UseQueryResult<CategoryIdsByProductType>[]) => ({
  results,
  isSuccess: results.every(result => result.isSuccess),
  isError: results.some(result => result.isError)
});

/**
 * Product type category ids per shop, for the given shops. Each shop's departments are fetched
 * once and cached (deduplicated across all callers via React Query). Without the public API URL
 * (e.g. Storybook) nothing is fetched and the result never succeeds, so no strike price is shown.
 */
export const useProductTypeCategoryIds = shopIds => {
  const $ = _c(4);
  const publicApiUrl = useAtomValue(publicApiUrlAtom);
  const uniqueShopIds = [...new Set(shopIds.map(String))];
  const {
    results,
    isSuccess,
    isError
  } = useQueries({
    queries: uniqueShopIds.map(shopId => ({
      ...productTypeDepartmentsQueryOptions(publicApiUrl ?? "", shopId),
      select: selectCategoryIds,
      enabled: Boolean(publicApiUrl)
    })),
    combine
  });
  const categoryIdsByShop = Object.fromEntries(uniqueShopIds.flatMap((shopId_0, i) => results[i]?.data ? [[shopId_0, results[i].data]] : []));
  let t0;
  if ($[0] !== categoryIdsByShop || $[1] !== isError || $[2] !== isSuccess) {
    t0 = {
      categoryIdsByShop,
      isSuccess,
      isError
    };
    $[0] = categoryIdsByShop;
    $[1] = isError;
    $[2] = isSuccess;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
};